import { Button, Chip, ErrorBanner, SkeletonRows, Textarea, toast } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import type { AutoAnswerRowOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { autoAnswersQuery, reasonText, useOverrideAutoAnswer, useUndoAutoAnswer } from './api'

function when(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : formatWhen(d.toISOString(), { style: 'absolute' })
}

function Reply({ label, text }: { label: string; text: string | null | undefined }) {
  return (
    <div className="min-w-0 rounded-md border border-border bg-muted/35 p-2.5">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-subtle">{label}</p>
      {text ? (
        <pre className="whitespace-pre-wrap font-sans text-sm leading-5">{text}</pre>
      ) : (
        <p className="text-sm text-muted-foreground">No sent reply recorded yet.</p>
      )}
    </div>
  )
}

function AnswerCard({ row }: { row: AutoAnswerRowOut }) {
  const review = useOverrideAutoAnswer()
  const undo = useUndoAutoAnswer()
  const [reason, setReason] = React.useState('')
  const busy = review.isPending || undo.isPending
  const score = row.agreement_distance == null ? null : Math.max(0, Math.round((1 - row.agreement_distance) * 100))
  const saveVerdict = (verdict: 'agree' | 'disagree') => {
    if (!reason.trim()) return
    review.mutate(
      { id: row.id, verdict, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`Marked ${verdict}`)
          setReason('')
        },
        onError: (error) => toast.error(`Not saved: ${describeError(error)}`),
      },
    )
  }
  const reverse = () => {
    if (!reason.trim()) return
    undo.mutate(
      { id: row.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('Automatic outcome marked reversed')
          setReason('')
        },
        onError: (error) => toast.error(`Not reversed: ${describeError(error)}`),
      },
    )
  }
  return (
    <li className="space-y-2 rounded-lg border border-border bg-card p-3" data-testid="auto-answer-row">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone={row.mode === 'auto' ? 'success' : 'primary'}>{row.mode === 'shadow' ? 'would answer' : 'auto'}</Chip>
        <Chip tone={row.guard_allowed === true ? 'success' : row.guard_allowed === false ? 'warning' : 'neutral'}>
          guard {row.guard_allowed === true ? 'passed' : row.guard_allowed === false ? 'blocked' : 'pending'}
        </Chip>
        <Chip tone="outline">{row.category}</Chip>
        {row.effective_verdict ? (
          <Chip tone={row.effective_verdict === 'agree' ? 'success' : 'danger'}>
            {row.effective_verdict}
            {row.manual_verdict ? ' · manual' : score === null ? '' : ` · ${score}%`}
          </Chip>
        ) : null}
        {row.substantive_added ? <Chip tone="warning">substantive info added</Chip> : null}
        <Link to="/case/$id" params={{ id: row.case_number }} className="ml-auto font-medium text-primary">
          {row.case_number}
        </Link>
      </div>
      <div>
        <p className="font-medium">{row.case_title}</p>
        <p className="text-xs text-subtle">
          Customer turn {when(row.inbound_at)} · recorded {when(row.created_at)} · {row.state.replaceAll('_', ' ')}
        </p>
      </div>
      <Reply label="Customer asked" text={row.inbound_body_text} />
      <div className="grid gap-2 md:grid-cols-2">
        <Reply label="Riley would have answered" text={row.proposed_body_text} />
        <Reply label="Person actually sent" text={row.actual_body_text} />
      </div>
      {row.kb_refs?.length ? <p className="text-xs text-muted-foreground">KB: {row.kb_refs.join(', ')}</p> : null}
      {row.hold_reason ? <p className="text-xs text-warning">Held: {reasonText(row.hold_reason)}</p> : null}
      {row.manual_reason ? <p className="text-xs text-muted-foreground">Review note: {row.manual_reason}</p> : null}
      <div className="space-y-1.5 border-t border-border pt-2">
        <Textarea
          aria-label={`Review note for ${row.case_number}`}
          value={reason}
          disabled={busy}
          placeholder="Why you agree, disagree, or are reversing this outcome"
          className="min-h-14"
          onChange={(event) => setReason(event.target.value)}
        />
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" disabled={busy || !reason.trim()} onClick={() => saveVerdict('agree')}>
            Mark agree
          </Button>
          <Button size="sm" variant="outline" disabled={busy || !reason.trim()} onClick={() => saveVerdict('disagree')}>
            Mark disagree
          </Button>
          {row.can_undo ? (
            <Button size="sm" variant="destructive" disabled={busy || !reason.trim()} onClick={reverse}>
              Reverse outcome
            </Button>
          ) : null}
        </div>
        {row.can_undo ? (
          <p className="text-xs text-muted-foreground">
            Available until {when(row.undo_until)}. Reversing updates the learning record; it cannot recall the delivered email.
          </p>
        ) : null}
      </div>
    </li>
  )
}

export function AutoAnswerPanel() {
  const q = useQuery(autoAnswersQuery())
  if (q.isPending) return <SkeletonRows count={3} />
  if (q.isError) {
    return <ErrorBanner title="Couldn't load Riley's answer comparisons" detail={describeError(q.error)} onRetry={() => void q.refetch()} />
  }
  if (!q.data.length) {
    return (
      <p className="py-3 text-sm text-muted-foreground">
        No safe-category turns have reached the shadow lane yet. Riley will compare its proposed answer with the person's later reply.
      </p>
    )
  }
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Shadow rows run the blocking guard over the exact composed reply but send nothing. Agreement compares normalized intent and flags
        substantive additions.
      </p>
      <ul className="space-y-2">
        {q.data.map((row) => (
          <AnswerCard key={row.id} row={row} />
        ))}
      </ul>
    </div>
  )
}
